import ConfirmModal from 'components/ConfirmModal'
import { FormTip, IconCheck } from 'components/form'
import { addDays, addHours, differenceInMinutes, isSameDay } from 'date-fns'
import NotifyModal from './NotifyModal'
import PropTypes from 'prop-types'
import { useContext, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { rescheduleOrder } from 'state/Orders'
import { Alerts, FMT, newDate, storeRefs } from 'utils'
import OrderContext from '../_context'

export default function RescheduleModal({ show, doClose }) {
	const MAX_DIFF           = 2 //hours
	const [initial, options] = NotifyModal.useOptions()

	const { t }           = useTranslation()
	const dispatch        = useDispatch()
	const order           = useContext(OrderContext)
	const notifyState     = useState(initial)
	const [refs, storage] = storeRefs()

	//TODO check if this time cap is working fine after timezone issues are solved
	// eslint-disable-next-line @miovision/disallow-date/no-new-date
	const min = new Date(Math.max(newDate().getTime(), addHours(order.ready_at, -MAX_DIFF).getTime()))
	const max = addHours(order.ready_at, MAX_DIFF)

	/** Indicates if we need to test for another date when validating the given time. `0` if `min == max == order.ready_at`, `+1` if `max != order.ready_at`, `-1` otherwise */
	const checkOtherDay = isSameDay(order.ready_at, min) && isSameDay(order.ready_at, max)? 0 : (isSameDay(order.ready_at, max)? -1 : 1)

	async function doSubmit() {
		const [h, m] = refs.time.value.split(':').map(x => parseInt(x))

		// eslint-disable-next-line @miovision/disallow-date/no-new-date
		let completeTime = new Date(order.ready_at.getFullYear(), order.ready_at.getMonth(), order.ready_at.getDate(), h, m)

		//no need to validate if some smart-ass unchecked the radio via console, they can't tamper the internal state 😎
		if (refs.time.value) {
			const validate = time => time.getTime() >= min.getTime() && time.getTime() <= max.getTime()
			if (!validate(completeTime)) {
				completeTime = addDays(completeTime, checkOtherDay) //if we leave intact, completeTime will be in the final date
				if (!checkOtherDay || !validate(completeTime)) {
					refs.time.setCustomValidity(t('Outside of min/max range'))
					return false
				}
			}
		}
		refs.time.setCustomValidity('') //safe to clear validation state, since otherwise we'll have left the function by now

		const diffMinutes = differenceInMinutes(completeTime, order.ready_at)
		const notifyMedium = notifyState[0]

		await dispatch(rescheduleOrder(order.id, diffMinutes, notifyMedium))
			.then(() => Alerts.success(t('Order rescheduled and message sent to customer.')))
			.finally(doClose)
	}

	return <ConfirmModal {...{ show, doClose, doSubmit }}
	                     confirmTexts={[t('Reschedule'), t('Rescheduling')]}
	                     confirmIcon={['far', 'calendar-edit']}
	                     title={t('Reschedule order')}>
		<Form>
			<Form.Group controlId="time">
				<Form.Label>{t('What\'s the new time?')}</Form.Label>
				<Form.Control type="time" required ref={storage} defaultValue={FMT.time(order.ready_at, { is24: true })}/>
				<FormTip><Trans>Between <tt>{{ minTime: FMT.time(min) }}</tt> and <tt>{{ maxTime: FMT.time(max) }}</tt>.</Trans></FormTip>
				{/* can't use min/max in input[type=time] reliably for validation, because Safari https://is.gd/E0KfMv */}
			</Form.Group>

			<Form.Group controlId="notify">
				<Form.Label>{t('Notify customer via:')}</Form.Label>
				<IconCheck.Group radio name="notify" statePair={notifyState} {...{ options, initial }}/>
			</Form.Group>
		</Form>
	</ConfirmModal>
}

RescheduleModal.propTypes = {
	show   : PropTypes.bool.isRequired,
	doClose: PropTypes.func.isRequired,
}
